import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

interface ExternalPostDto {
  title: string;
  link: string;
  content?: string;
  published?: string;
}

interface BlogPostViewModel {
  title: string;
  description: string;
  image: string | null;
  dateIso: string;
  dateLabel: string;
  link: string;
  authorName: string;
  authorInitial: string;
  readTimeLabel: string;
  category: string;
}

@Component({
  selector: 'app-our-blog',
  standalone: true,
  imports: [CommonModule, HttpClientModule, TranslatePipe],
  templateUrl: './our-blog.component.html',
  styleUrl: './our-blog.component.scss',
})
export class OurBlogComponent implements OnInit {
  private readonly http = inject(HttpClient);

  posts: BlogPostViewModel[] = [];
  isLoading = false;
  error: string | null = null;
  totalPosts = 0;

  ngOnInit(): void {
    this.fetchPosts();
  }

  private fetchPosts(): void {
    this.isLoading = true;
    this.error = null;

    this.http
      .get<ExternalPostDto[]>(`https://meduimapibrocom.onrender.com/posts`)
      .subscribe({
        next: (data) => {
          const mapped = (data ?? []).map((p) => {
            const contentHtml = (p.content || '').toString();
            const text = this.extractText(contentHtml);
            const author = this.extractAuthorFromLink(p.link);
            const minutes = this.estimateReadTimeMinutes(text);
            const iso = p.published || '';
            return {
              title: p.title,
              description: text.slice(0, 220),
              image: this.extractFirstImageUrl(contentHtml),
              dateIso: iso,
              dateLabel: this.formatDate(iso),
              link: p.link,
              authorName: author,
              authorInitial: author ? author.charAt(0).toUpperCase() : 'A',
              readTimeLabel: minutes + ' min read',
              category: this.extractCategoryFromLink(p.link),
            } as BlogPostViewModel;
          });

          // Sort by published date desc and take the latest six
          mapped.sort((a, b) => {
            const ta = a.dateIso ? new Date(a.dateIso).getTime() : 0;
            const tb = b.dateIso ? new Date(b.dateIso).getTime() : 0;
            return tb - ta;
          });

          this.totalPosts = mapped.length;
          this.posts = mapped.slice(0, 6);
          this.isLoading = false;
        },
        error: () => {
          this.error = 'Failed to load blog posts.';
          this.isLoading = false;
        },
      });
  }

  private extractText(html: string): string {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
    return text.replace(/\s+/g, ' ').trim();
  }

  private extractFirstImageUrl(html: string): string | null {
    if (!html) return null;
    const imgTagMatch = html.match(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i);
    if (imgTagMatch && imgTagMatch[1]) {
      if (!/post\.clientViewed/.test(imgTagMatch[1])) return imgTagMatch[1];
    }
    const allImgMatches = [...html.matchAll(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi)];
    for (const m of allImgMatches) {
      const url = m[1];
      if (!/post\.clientViewed/.test(url) && !/width=\"1\"/.test(m[0])) {
        return url;
      }
    }
    return null;
  }

  private extractAuthorFromLink(link: string): string {
    if (!link) return 'Author';
    const m = link.match(/medium\.com\/@([^/]+)/i);
    if (m && m[1]) {
      return m[1];
    }
    return 'Author';
  }

  private estimateReadTimeMinutes(text: string): number {
    if (!text) return 1;
    const words = text.split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return minutes;
  }

  private formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  }

  private extractCategoryFromLink(link: string): string {
    try {
      const url = new URL(link);
      return url.hostname.replace('www.', '');
    } catch {
      return 'Article';
    }
  }
}
